import React from 'react';
import {
    Center,
    VStack,
    HStack,
    Text,
    Spinner,
    Box,
    Divider,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon, NotAllowedIcon } from '@chakra-ui/icons';

const StepIcon = ({ status }) => {
    if (status === 'done') {
        return <CheckCircleIcon color="green.400" boxSize="18px" />;
    }
    if (status === 'error') {
        return <WarningIcon color="red.400" boxSize="18px" />;
    }
    if (status === 'loading') {
        return (
            <Spinner
                size="sm"
                thickness="2px"
                speed="0.65s"
                color="blue.300"
                emptyColor="blue.50"
            />
        );
    }
    return (
        <Box
            w="18px"
            h="18px"
            borderRadius="full"
            border="1.5px solid"
            borderColor="gray.300"
            flexShrink={0}
        />
    );
};

const stepColor = (status) => {
    switch (status) {
        case 'done':    return 'green.500';
        case 'error':   return 'red.500';
        case 'loading': return 'blue.300';
        default:        return 'gray.400';
    }
};

const Preloader = ({ steps, failedStep }) => {
    const hasError = !!failedStep;

    return (
        <Center h="100vh" flexDirection="column" gap={6} bg="white">

            {/* Spinner — stops and turns red on failure */}
            {hasError
                ? <NotAllowedIcon color="red.400" boxSize="48px" />
                : (
                    <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="gray.200"
                        color="blue.300"
                        size="xl"
                    />
                )
            }

            {/* Step list */}
            {steps && steps.length > 0 && (
                <VStack spacing={3} align="flex-start" minW="240px">
                    {steps.map((step) => (
                        <HStack
                            key={step.id}
                            spacing={3}
                            opacity={step.status === 'pending' ? 0.3 : 1}
                            transition="opacity 0.3s"
                        >
                            <StepIcon status={step.status} />
                            <Text
                                fontSize="sm"
                                fontWeight="500"
                                color={stepColor(step.status)}
                                transition="color 0.3s"
                            >
                                {step.label}
                            </Text>
                        </HStack>
                    ))}
                </VStack>
            )}

            {/* Hard-block error message */}
            {hasError && (
                <>
                    <Divider maxW="340px" borderColor="red.100" />
                    <VStack spacing={2} maxW="340px" textAlign="center" px={4}>
                        <Text fontSize="sm" fontWeight="700" color="red.600">
                            System initialisation failed
                        </Text>
                        <Text fontSize="xs" color="gray.600" lineHeight="tall">
                            <Text as="span" fontWeight="600" color="gray.800">
                                {failedStep}
                            </Text>
                            {' '}could not be completed. The system cannot start without this step.
                        </Text>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                            Please restart the application. If this problem persists, check the server connection and try again.
                        </Text>
                    </VStack>
                </>
            )}

        </Center>
    );
};

export default Preloader;
